import React from 'react';
import { NavLink } from 'react-router-dom';
import { 
  HomeIcon, 
  ArchiveBoxIcon, 
  ChartBarIcon,
  Cog6ToothIcon,
  UserIcon,
  ArrowLeftOnRectangleIcon,
  UsersIcon,
  BriefcaseIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';

const Sidebar = ({ onLogout }) => {
  const { user, logout, loading } = useAuth();
  const isAdmin = user?.roles?.includes('ADMIN');

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      // Даже если logout не удался, очищаем локальное состояние
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
    window.location.href = '/login';
    }
  };

  const menuItems = [
    {
      path: '/',
      name: 'Управление проектами',
      icon: HomeIcon,
      description: 'Основная доска задач',
      end: true
    },
    {
      path: '/archive',
      name: 'Архив',
      icon: ArchiveBoxIcon,
      description: 'Завершенные задачи'
    },
    {
      path: '/profile',
      name: 'Профиль',
      icon: UserIcon,
      description: 'Личные данные'
    },
    {
      path: '/bugs',
      name: 'Баги',
      icon: ExclamationTriangleIcon,
      description: 'Сообщения с тегом #баг'
    },
    // Пункт управления пользователями только для администраторов
    ...(isAdmin ? [{
      path: '/users',
      name: 'Управление пользователями',
      icon: UsersIcon,
      description: 'Добавление и удаление пользователей'
    }] : []),
    {
      path: '/analytics',
      name: 'Аналитика',
      icon: ChartBarIcon,
      description: 'Статистика и отчеты'
    },
    // Пункт настроек только для администраторов
    ...(isAdmin ? [{
      path: '/settings',
      name: 'Настройки',
      icon: Cog6ToothIcon,
      description: 'Конфигурация системы'
    }] : []),
    // Пункт проектов только для администраторов
    ...(isAdmin ? [{
      path: '/projects',
      name: 'Проекты',
      icon: BriefcaseIcon,
      description: 'Список и управление проектами'
    }] : [])
  ];

  return (
    <div className="fixed left-0 top-0 h-full w-20 bg-gradient-to-b from-blue-600 to-blue-800 shadow-xl z-50">
      {/* Logo */}
      <div className="flex flex-col items-center justify-center border-b border-blue-500/30 pb-2 pt-2">
        <div className="flex items-center justify-center w-12 h-12 bg-white rounded-lg shadow-lg">
          <span className="text-blue-600 font-bold text-base tracking-tight">TMS</span>
        </div>
        {/* User info или скелетон */}
        {loading ? (
          <div className="mt-2 text-center w-full animate-pulse">
            <div className="h-4 bg-blue-300/40 rounded w-3/4 mx-auto mb-2" />
            <div className="h-3 bg-blue-200/40 rounded w-1/2 mx-auto" />
          </div>
        ) : user && (
          <div className="mt-2 text-center w-full">
            <div className="text-xs font-semibold text-white truncate max-w-[80px] mx-auto">
              {user.firstName || user.username || user.email}
            </div>
            <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-medium mt-1 ${isAdmin ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
              {isAdmin ? 'Admin' : 'User'}
            </div>
          </div>
        )}
      </div>

      {/* Navigation или скелетон */}
      <nav className="mt-8 px-2">
        <ul className="space-y-2">
          {loading ? (
            // Скелетон для меню
            Array.from({ length: 5 }).map((_, i) => (
              <li key={i}>
                <div className="h-10 w-full bg-blue-300/30 rounded-xl animate-pulse mb-2" />
              </li>
            ))
          ) : (
            menuItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <li key={item.path}>
                  <NavLink
                  to={item.path}
                    end={item.end || false}
                    className={({ isActive }) => `
                    group relative flex items-center justify-center h-12 w-full rounded-xl
                    transition-all duration-300 ease-in-out
                    ${isActive 
                      ? 'bg-white text-blue-600 shadow-lg transform scale-105' 
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                    }
                  `}
                  title={item.description}
                >
                  <IconComponent className="w-6 h-6 transition-transform duration-300 group-hover:scale-110" />
                  {/* Tooltip */}
                  <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-50">
                    {item.name}
                  </div>
                  </NavLink>
              </li>
            );
            })
          )}
        </ul>
      </nav>

      {/* Bottom section */}
      <div className="absolute bottom-0 left-0 right-0 p-2">
        <button
          onClick={handleLogout}
          className="group relative flex items-center justify-center h-12 w-full rounded-xl text-white/80 hover:text-white hover:bg-white/10 transition-all duration-300"
          title="Выйти из системы"
        >
          <ArrowLeftOnRectangleIcon className="w-6 h-6 transition-transform duration-300 group-hover:scale-110" />
          {/* Tooltip */}
          <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-50">
            Выйти
          </div>
        </button>
      </div>
    </div>
  );
};

export default Sidebar; 